import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import PoseCard from './PoseCard';
import PhotoRail from './PhotoRail';
import SeasonCarousel from './SeasonCarousel';
import PackageSelection from './PackageSelection';
import PackageBuilder from './PackageBuilder';
import CartBar from './CartBar';
import CartView from './CartView';
import RetouchingUpsell from './RetouchingUpsell';
import YearbookUpsell from './YearbookUpsell';
import PastPhotosUpsell from './PastPhotosUpsell';
import PackageAddedConfirmation from './PackageAddedConfirmation';
import PrintessEditor from './PrintessEditor';
import Lightbox from './Lightbox';
import ToastStack from './ToastStack';
import styles from './styles';
import galleryCss from './galleryCss';

// Configurable post-add upsell steps, in default order. The admin can reorder /
// disable these per school (/admin/shop/upsell); the server passes the resolved
// list as `upsellSteps` and chainNext walks ['add', ...upsellSteps] -> cart.
const DEFAULT_UPSELL_STEPS = ['retouching', 'yearbook', 'pastphotos', 'siblings'];
// The post-add chain for an add: ['add', ...known steps]. Steps come from the
// product just added (server-resolved: product -> school -> global), defaulting
// to the page-level upsellSteps.
const buildChain = (steps) => ['add', ...(steps || []).filter((k) => DEFAULT_UPSELL_STEPS.includes(k))];

const readFavorites = (key) => {
  try { return new Set(JSON.parse(window.localStorage.getItem(key) || '[]')); } catch (e) { return new Set(); }
};

const CODE_NOTE_KEY = 'shopCodeNoteDismissed';
const readBool = (key) => {
  try { return window.localStorage.getItem(key) === '1'; } catch (e) { return false; }
};

// Multi-season gallery. Shows the student's seasons as tabs; the current senior
// season polls for poses as EC2 produces them (4s→30s backoff, like
// DownloadImages), while prior years load once. Watermarked previews only;
// full-res is gated behind purchase. Products are season-aware: the senior
// season sells packages, prior years sell past-images prints.
const FAST_INTERVAL = 4000;
const SLOW_INTERVAL = 30000;
const FAST_WINDOW_MS = 90000;

const StoreGallery = ({
  posesUrl, productsUrl, templatesUrl, seasonsUrl, studentsUrl, cartUrl, cartItemsUrl,
  couponUrl, couponDeleteUrl, checkoutUrl,
  csrfToken, studentName, currentStudentImageId, printessToken, upsellSteps, mode,
}) => {
  const [seasons, setSeasons] = useState([]);
  const [seasonsLoaded, setSeasonsLoaded] = useState(false); // gate the flow landing until seasons resolve
  const [studentList, setStudentList] = useState([]); // session students (self + siblings)
  const [studentBaseId, setStudentBaseId] = useState(currentStudentImageId); // selected student's senior image
  const [activeId, setActiveId] = useState(currentStudentImageId);
  const [poses, setPoses] = useState([]);
  const [bgUrl, setBgUrl] = useState(null); // default background shown behind poses
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]); // display templates for the season
  const [addons, setAddons] = useState([]); // retouching etc., upsold after build
  // { products: [...yearbook products], personalization: { variant_id, price } }
  const [yearbook, setYearbook] = useState({ products: [], personalization: null });
  const [printess, setPrintess] = useState([]); // Printess cards & gifts
  const [printessProduct, setPrintessProduct] = useState(null); // the card/gift being personalized
  const [status, setStatus] = useState('loading'); // loading | ready | empty | error
  const [selectedPose, setSelectedPose] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [retouchOpId, setRetouchOpId] = useState(null); // cart line being offered retouching
  // Display style (LookTemplate id) is chosen per-product in the builder; one-click
  // products auto-apply their default. No global selection here.
  // gallery | packages | builder | retouching | yearbook | pastphotos | siblings | printess | cart
  const [view, setView] = useState('gallery');
  const [seasonOpen, setSeasonOpen] = useState(false); // flow: drilled into a season's poses
  const [pendingLightbox, setPendingLightbox] = useState(false); // open favorites once a season loads
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [cart, setCart] = useState({ item_count: 0, total: null });
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [adding, setAdding] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [codeNoteHidden, setCodeNoteHidden] = useState(() => readBool(CODE_NOTE_KEY));

  // Toasts: transient confirmations/errors that auto-dismiss (replaces inline
  // flash/cue banners). A monotonic counter keys them (no Date.now collisions).
  const toastSeq = useRef(0);
  const dismissToast = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);
  const addToast = useCallback((message, type = 'success') => {
    toastSeq.current += 1;
    const id = toastSeq.current;
    setToasts((list) => [...list, { id, message, type }]);
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 4000);
  }, []);

  // Tracks which ready poses we've already shown, per season, so the poll can
  // announce only genuinely new arrivals (not the initial load).
  const seenReadyIds = useRef(new Set());
  const seenInitialized = useRef(false);

  // In-memory, per-tab stale-while-revalidate cache keyed by student_image_id so
  // toggling back to an already-loaded season renders instantly (no blank
  // skeleton) while a fresh fetch revalidates in the background. NOT persisted to
  // localStorage on purpose: it holds gated image URLs that must not hit disk.
  const posesCache = useRef({});
  const productsCache = useRef({});
  const templatesCache = useRef({});

  // Compare a freshly-polled list against what we've already shown and surface a
  // transient "new photos arrived" toast. The first load just seeds the baseline.
  const announceNewPoses = useCallback((list) => {
    const readyIds = list.filter((p) => p.ready).map((p) => p.id);
    if (!seenInitialized.current) {
      seenReadyIds.current = new Set(readyIds);
      seenInitialized.current = true;
      return;
    }
    const fresh = readyIds.filter((id) => !seenReadyIds.current.has(id));
    if (fresh.length) {
      fresh.forEach((id) => seenReadyIds.current.add(id));
      addToast(`${fresh.length} new photo${fresh.length === 1 ? '' : 's'} added`, 'info');
    }
  }, [addToast]);

  const dismissCodeNote = useCallback(() => {
    setCodeNoteHidden(true);
    try { window.localStorage.setItem(CODE_NOTE_KEY, '1'); } catch (e) { /* ignore */ }
  }, []);

  // Flow navigation between the full-page views (mirrors the Figma). These use
  // only setters, so they're defined up here for the Esc handler to reference.
  const shopPackages = useCallback((pose) => {
    setSelectedPose(pose);
    setSelectedVariant(null);
    setView('packages');
  }, []);
  const backToGallery = useCallback(() => {
    setView('gallery');
    setSelectedPose(null);
    setSelectedVariant(null);
  }, []);

  // Favorites are scoped to the season being viewed (pose IDs only exist within
  // their own season), so the key follows the active tab, not the senior image.
  const favKey = `shopFavorites:${activeId}`;
  const [favorites, setFavorites] = useState(() => readFavorites(favKey));
  // Reload the right season's favorites when the tab changes.
  useEffect(() => { setFavorites(readFavorites(favKey)); }, [favKey]);
  // Start each full-page view at the top so its header isn't left scrolled
  // under the sticky shop header when switching views.
  useEffect(() => { window.scrollTo(0, 0); }, [view]);
  const toggleFavorite = useCallback((poseId) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(poseId)) next.delete(poseId); else next.add(poseId);
      try { window.localStorage.setItem(favKey, JSON.stringify([...next])); } catch (e) { return next; }
      return next;
    });
  }, [favKey]);
  // Favorite every pose id passed in (the lightbox's "Select all as favorites").
  const favoriteAll = useCallback((poseIds) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      poseIds.forEach((id) => next.add(id));
      try { window.localStorage.setItem(favKey, JSON.stringify([...next])); } catch (e) { return next; }
      return next;
    });
  }, [favKey]);

  // Polling/"current" is relative to the SELECTED student's senior season.
  const isCurrent = activeId === studentBaseId;
  const withId = (url) => `${url}${url.includes('?') ? '&' : '?'}student_image_id=${activeId}`;

  // Reflect an existing cart in the sticky bar on load (a returning customer may
  // already have items).
  useEffect(() => {
    let active = true;
    fetch(cartUrl, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setCart({ item_count: d.item_count, total: d.total }); })
      .catch(() => {});
    return () => { active = false; };
  }, [cartUrl]);

  // Keep the shared header's cart badge (server-rendered) in sync as the island
  // adds/removes items, so the count is live without a full-page reload.
  useEffect(() => {
    const badge = document.getElementById('shop-cart-count');
    if (!badge) return;
    const count = cart.item_count || 0;
    badge.textContent = count;
    badge.dataset.count = count;
    badge.hidden = count === 0;
  }, [cart.item_count]);

  // Session students (self + POC-linked siblings) for the student switcher.
  useEffect(() => {
    let active = true;
    fetch(studentsUrl, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : { students: [] }))
      .then((d) => { if (active) setStudentList(d.students || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [studentsUrl]);

  // Season tabs/cards for the SELECTED student.
  useEffect(() => {
    let active = true;
    fetch(`${seasonsUrl}?student_image_id=${studentBaseId}`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : { seasons: [] }))
      .then((d) => { if (active) setSeasons(d.seasons || []); })
      .catch(() => {})
      .finally(() => { if (active) setSeasonsLoaded(true); });
    return () => { active = false; };
  }, [seasonsUrl, studentBaseId]);

  // Poses for the active season; poll only the current senior season.
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const controller = new AbortController();
    const startedAt = Date.now();
    const cached = posesCache.current[activeId];
    if (cached) {
      // Stale-while-revalidate: paint the cached season immediately, and seed the
      // arrival baseline from it so the revalidation doesn't re-announce old poses.
      setPoses(cached.poses);
      setBgUrl(cached.bgUrl);
      setStatus(cached.poses.length ? 'ready' : 'empty');
      seenReadyIds.current = new Set(cached.poses.filter((p) => p.ready).map((p) => p.id));
      seenInitialized.current = true;
    } else {
      setPoses([]); // drop the previous season's poses so they never linger
      setBgUrl(null);
      setStatus('loading');
      seenReadyIds.current = new Set();
      seenInitialized.current = false;
    }
    const url = withId(posesUrl);
    const run = async () => {
      try {
        const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const next = data.poses || [];
        announceNewPoses(next);
        // Keep the existing object (and its already-signed watermark URL) for poses
        // we've already shown ready, so re-polling doesn't change <img src> and
        // reload/flicker every image. Only newly-ready/changed poses take the new URL.
        setPoses((prev) => {
          const prevById = new Map(prev.map((p) => [p.id, p]));
          return next.map((p) => {
            const old = prevById.get(p.id);
            return old && old.ready && p.ready ? old : p;
          });
        });
        setBgUrl(data.default_background_url || null);
        setStatus(next.length ? 'ready' : 'empty');
        posesCache.current[activeId] = { poses: next, bgUrl: data.default_background_url || null };
      } catch (e) {
        if (cancelled || e.name === 'AbortError') return;
        setStatus((prev) => (prev === 'ready' ? 'ready' : 'error'));
      }
      if (cancelled || !isCurrent) return;
      const elapsed = Date.now() - startedAt;
      timer = setTimeout(run, elapsed < FAST_WINDOW_MS ? FAST_INTERVAL : SLOW_INTERVAL);
    };
    run();
    return () => { cancelled = true; controller.abort(); if (timer) clearTimeout(timer); };
  }, [activeId, posesUrl, isCurrent, announceNewPoses]);

  // Products for the active season (stale-while-revalidate from the per-season cache).
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const cached = productsCache.current[activeId];
    if (cached) setProducts(cached); // instant paint; revalidate below
    fetch(withId(productsUrl), { headers: { Accept: 'application/json' }, signal: controller.signal })
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((d) => {
        if (!active) return;
        const next = d.products || [];
        setProducts(next);
        setAddons(d.addons || []);
        setYearbook(d.yearbook || { products: [], personalization: null });
        setPrintess(d.printess || []);
        productsCache.current[activeId] = next;
      })
      .catch(() => {});
    return () => { active = false; controller.abort(); };
  }, [activeId, productsUrl]);

  // Published display templates for the active season. Read-only; selecting one
  // only changes how the photo is composed in the preview (no fulfillment impact).
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const cached = templatesCache.current[activeId];
    if (cached) setTemplates(cached);
    fetch(withId(templatesUrl), { headers: { Accept: 'application/json' }, signal: controller.signal })
      .then((r) => (r.ok ? r.json() : { templates: [] }))
      .then((d) => {
        if (!active) return;
        const next = d.templates || [];
        setTemplates(next);
        templatesCache.current[activeId] = next;
      })
      .catch(() => {});
    return () => { active = false; controller.abort(); };
  }, [activeId, templatesUrl]);

  // Lock background scroll while the lightbox overlay is open. The packages,
  // builder & cart views are full pages, not overlays.
  const overlayOpen = lightboxIndex != null;
  useEffect(() => {
    if (!overlayOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [overlayOpen]);
  // Esc steps back through the full-page flow (the lightbox self-handles Esc).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (view === 'builder') setView('packages');
      else if (['retouching', 'yearbook', 'pastphotos', 'siblings'].includes(view)) setView('cart');
      else if (view === 'packages' || view === 'cart') backToGallery();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view, backToGallery]);

  const switchSeason = (id) => {
    setActiveId(id);
    setSelectedPose(null);
    setSelectedVariant(null);
    setView('gallery');
    setLightboxIndex(null);
    setFavoritesOnly(false); // favorites are per-season; show the full grid first
  };

  // Flow gallery (Figma): the landing shows season cards. Opening a season brings
  // up the favorite-selection lightbox directly (resolved once its poses load —
  // see the effect below); if nothing is ready yet we fall back to the pose view.
  const openSeason = (id) => {
    // Switching to a DIFFERENT season: eagerly mark its poses as loading so the
    // lightbox opens only once THAT season's poses arrive. Otherwise the pending
    // effect can open with the previous season's still-loaded poses, then
    // setPoses([]) for the new season clears them (close), then they reload
    // (reopen) — the open/close/reopen flicker.
    if (id !== activeId) { setPoses([]); setStatus('loading'); }
    switchSeason(id);
    setPendingLightbox(true);
  };
  const backToSeasons = () => { setSeasonOpen(false); setLightboxIndex(null); };

  // Switch to a sibling: their senior season becomes the base (drives seasons,
  // polling, catalog). Cart is shared; lines carry each student's id server-side.
  const switchStudent = (id) => {
    setStudentBaseId(id);
    switchSeason(id);
    setSeasonOpen(false); // back to the season chooser for the newly-selected student
  };

  const addToCart = useCallback(async (variant, opts = {}) => {
    if (!selectedPose && !opts.slots) return false;
    setAdding(true);
    try {
      const res = await fetch(cartItemsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          variant_id: variant.id,
          student_image_id: activeId,
          pose_id: selectedPose?.id,
          slots: opts.slots,
          yearbook_pose_id: opts.yearbookPoseId,
          template_id: opts.templateId ?? null, // chosen display style (null = plain photo)
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not add to cart.');
      setCart({ item_count: data.item_count, total: data.total });
      addToast(`Added ${variant.name} to your cart`, 'success');
      return data.order_package_id || null;
    } catch (e) {
      addToast(e.message, 'error');
      return null;
    } finally {
      setAdding(false);
    }
  }, [selectedPose, cartItemsUrl, csrfToken, activeId, addToast]);

  // Chain for the product currently being added; set on each add so the upsell
  // sequence is the one configured for THAT product (falls back to upsellSteps).
  const activeChainRef = useRef(buildChain(upsellSteps));

  // Advance one step past the one just finished, skipping any step with nothing
  // to offer; fall through to the cart. Walks the active product's chain.
  const chainNext = useCallback((step) => {
    const priorSeasons = seasons.filter((se) => !se.current && (se.pose_count || 0) > 0);
    const siblings = studentList.filter((st) => st.student_image_id !== studentBaseId);
    const available = {
      retouching: addons.length > 0,
      yearbook: (yearbook.products?.length || 0) > 0,
      pastphotos: priorSeasons.length > 0,
      siblings: siblings.length > 0,
    };
    const chain = activeChainRef.current;
    const from = chain.indexOf(step);
    const next = chain.slice(from + 1).find((key) => available[key]);
    return setView(next || 'cart');
  }, [seasons, studentList, studentBaseId, addons.length, yearbook.products]);
  const afterAdd = useCallback((opId, variant) => {
    if (!opId) return;
    activeChainRef.current = buildChain(variant?.upsell_steps || upsellSteps);
    setRetouchOpId(opId); // the line a retouching add-on would attach to
    chainNext('add');
  }, [chainNext, upsellSteps]);

  // Picking a package: multi-print packages open the builder; a single-sheet
  // package is a one-click add (the pose is already chosen).
  const selectVariant = useCallback(async (variant) => {
    // Open the builder for multi-print packages OR any product that offers a
    // display style (e.g. a graduation card) so the customer can preview it.
    const styled = (variant.template_ids || []).length > 0;
    if (variant.multi || styled) {
      setSelectedVariant(variant);
      setView('builder');
      return;
    }
    afterAdd(await addToCart(variant, { templateId: null }), variant);
  }, [addToCart, afterAdd]);
  const confirmBuild = useCallback(async (opts) => {
    afterAdd(await addToCart(selectedVariant, opts), selectedVariant);
  }, [addToCart, selectedVariant, afterAdd]);

  // Attach a retouching add-on to the just-built line, then continue the chain.
  const addRetouching = useCallback(async (variant) => {
    setAdding(true);
    try {
      const res = await fetch(`${cartItemsUrl}/${retouchOpId}/addons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ variant_id: variant.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not add retouching.');
      setCart({ item_count: data.item_count, total: data.total });
      addToast(`Added ${variant.name} retouching`, 'success');
      chainNext('retouching');
    } catch (e) {
      addToast(e.message, 'error');
    } finally {
      setAdding(false);
    }
  }, [cartItemsUrl, retouchOpId, csrfToken, addToast, chainNext]);

  // Batch-add yearbooks (+ optional personalization) for the selected students,
  // then go to the cart. `selections` = [{ student_image_id, variant_id, personalize }].
  const addYearbooks = useCallback(async (selections) => {
    if (!selections.length) { chainNext('yearbook'); return; }
    setAdding(true);
    try {
      const res = await fetch(`${cartItemsUrl}/yearbooks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ selections }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not add yearbook.');
      setCart({ item_count: data.item_count, total: data.total });
      const count = selections.length;
      addToast(`Added ${count} yearbook${count === 1 ? '' : 's'} to your cart`, 'success');
      chainNext('yearbook');
    } catch (e) {
      addToast(e.message, 'error');
    } finally {
      setAdding(false);
    }
  }, [cartItemsUrl, csrfToken, addToast, chainNext]);

  // Open the Printess editor for a card/gift (personalized with the chosen pose).
  const openPrintess = useCallback((product, pose) => {
    setSelectedPose(pose || null);
    setPrintessProduct(product);
    setView('printess');
  }, []);
  // Editor "add to basket": persist the design's save token + thumbnail on a cart line.
  const addPrintess = useCallback(async (saveToken, thumbnailUrl) => {
    if (!printessProduct) return;
    setAdding(true);
    try {
      const res = await fetch(`${cartItemsUrl}/printess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          variant_id: printessProduct.variants[0]?.id,
          student_image_id: activeId,
          pose_id: selectedPose?.id,
          save_token: saveToken,
          thumbnail_url: thumbnailUrl,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not add your design.');
      setCart({ item_count: data.item_count, total: data.total });
      addToast(`Added ${printessProduct.name} to your cart`, 'success');
      setView('cart');
    } catch (e) {
      addToast(e.message, 'error');
    } finally {
      setAdding(false);
    }
  }, [printessProduct, cartItemsUrl, csrfToken, activeId, selectedPose, addToast]);

  const allReadyPoses = poses.filter((p) => p.ready);
  const readyCount = allReadyPoses.length;
  const pendingCount = poses.length - readyCount;
  const enteredLabel = seasons.find((s) => s.current)?.label;
  const activeStudentName = studentList.find((st) => st.student_image_id === studentBaseId)?.name || studentName;
  // Favorites filter: when on, the grid + lightbox show only favorited poses.
  // The shop panel still offers every pose (you can build with any photo).
  const displayed = favoritesOnly ? poses.filter((p) => favorites.has(p.id)) : poses;
  const displayedReady = displayed.filter((p) => p.ready);
  // Merchandising: lowest sellable price across the season's catalog.
  const allVariants = products.flatMap((p) => p.variants);
  const variantPrices = allVariants.map((v) => v.price).filter((n) => n > 0);
  const startingPrice = variantPrices.length ? Math.min(...variantPrices) : null;
  // "Popular packages" strip (flow gallery): badged variants, else priciest few.
  const featuredVariants = (() => {
    const badged = allVariants.filter((v) => v.badge);
    return (badged.length ? badged : [...allVariants].sort((a, b) => (b.price || 0) - (a.price || 0))).slice(0, 4);
  })();
  const openLightbox = (pose) => {
    const idx = displayedReady.findIndex((p) => p.id === pose.id);
    if (idx >= 0) setLightboxIndex(idx);
  };

  // Resolve an openSeason() request once the season's poses settle: open the
  // favorite-selection lightbox if anything is ready, else fall back to the pose
  // view (showing the "preparing your photos" state for a still-processing season).
  useEffect(() => {
    if (!pendingLightbox || status === 'loading') return;
    if (displayedReady.length > 0) setLightboxIndex(0);
    else setSeasonOpen(true);
    setPendingLightbox(false);
  }, [pendingLightbox, status, displayedReady.length]);

  // Post-build retouching upsell.
  if (view === 'retouching') {
    return (
      <>
        <style>{galleryCss}</style>
        <RetouchingUpsell
          addons={addons}
          studentName={activeStudentName}
          adding={adding}
          onSelect={addRetouching}
          onSkip={() => chainNext('retouching')}
        />
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // Post-retouching yearbook upsell (per-student yearbook + personalization).
  if (view === 'yearbook') {
    const ybStudents = studentList.length
      ? studentList
      : [{ student_image_id: studentBaseId, name: activeStudentName }];
    return (
      <>
        <style>{galleryCss}</style>
        <YearbookUpsell
          students={ybStudents}
          variants={(yearbook.products || []).flatMap((p) => p.variants)}
          personalization={yearbook.personalization}
          adding={adding}
          onSubmit={addYearbooks}
          onSkip={() => chainNext('yearbook')}
        />
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // Pre-checkout cross-sell: the current student's prior seasons that still have
  // photos. "View & order" opens that season; "No thanks" continues the chain.
  if (view === 'pastphotos') {
    const priorSeasons = seasons.filter((se) => !se.current && (se.pose_count || 0) > 0);
    return (
      <>
        <style>{galleryCss}</style>
        <PastPhotosUpsell
          seasons={priorSeasons}
          studentName={activeStudentName}
          adding={adding}
          onView={(id) => openSeason(id)}
          onSkip={() => chainNext('pastphotos')}
        />
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // Post-add sibling cross-sell: order for the family's other students.
  if (view === 'siblings') {
    const siblings = studentList.filter((st) => st.student_image_id !== studentBaseId);
    return (
      <>
        <style>{galleryCss}</style>
        <PackageAddedConfirmation
          studentName={activeStudentName}
          siblings={siblings}
          adding={adding}
          onShop={(id) => switchStudent(id)}
          onSkip={() => chainNext('siblings')}
        />
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // Printess editor for a personalized card/gift.
  if (view === 'printess' && printessProduct) {
    return (
      <>
        <style>{galleryCss}</style>
        <PrintessEditor
          token={printessToken}
          product={printessProduct}
          photoUrl={selectedPose?.watermark_url}
          adding={adding}
          onBasket={addPrintess}
          onBack={backToGallery}
        />
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // Full-page cart.
  if (view === 'cart') {
    return (
      <>
        <style>{galleryCss}</style>
        <CartView
          cartUrl={cartUrl}
          cartItemsUrl={cartItemsUrl}
          posesUrl={posesUrl}
          couponUrl={couponUrl}
          couponDeleteUrl={couponDeleteUrl}
          checkoutUrl={checkoutUrl}
          csrfToken={csrfToken}
          onBack={backToGallery}
          onChange={(summary) => setCart((c) => ({ ...c, ...summary }))}
        />
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // Full-page package selection.
  if (view === 'packages' && selectedPose) {
    return (
      <>
        <style>{galleryCss}</style>
        <PackageSelection
          pose={selectedPose}
          products={products}
          backgroundUrl={bgUrl}
          templates={templates}
          studentName={activeStudentName}
          adding={adding}
          onBack={backToGallery}
          onSelect={selectVariant}
        />
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // Full-page package builder (per-sheet pose + background).
  if (view === 'builder' && selectedVariant && selectedPose) {
    return (
      <>
        <style>{galleryCss}</style>
        <PackageBuilder
          variant={selectedVariant}
          backgrounds={selectedVariant.backgrounds || []}
          defaultBackgroundId={selectedVariant.defaultBackgroundId || null}
          poses={allReadyPoses}
          backgroundUrl={bgUrl}
          templates={templates.filter((tpl) => (selectedVariant.template_ids || []).includes(tpl.id))}
          defaultTemplateId={!selectedVariant.multi ? (selectedVariant.template_ids || [])[0] || null : null}
          templateRequired={!!selectedVariant.template_required}
          studentName={activeStudentName}
          adding={adding}
          onConfirm={confirmBuild}
          onBack={() => setView('packages')}
        />
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // Flow gallery landing = the season-card chooser (only worth showing with more
  // than one season). Drilling into a season (or the direct page) shows its poses.
  const showSeasonCards = mode === 'flow' && seasons.length > 1 && !seasonOpen;
  // While the flow landing waits on the seasons fetch, hold off rendering the pose
  // rail so it doesn't flash before the season chooser resolves.
  const seasonsPending = mode === 'flow' && !seasonsLoaded && !seasonOpen;

  return (
    // Pad the bottom when the fixed CartBar is shown so it never covers the last
    // content (e.g. the "Personalize a card or gift" cards' buttons).
    <div style={cart.item_count ? { ...styles.wrap, paddingBottom: '6rem' } : styles.wrap}>
      <style>{galleryCss}</style>

      {mode === 'flow' && (
        <div style={styles.galleryHead}>
          <h1 style={styles.galleryTitle}>
            {activeStudentName ? `${activeStudentName}'s Gallery` : 'Your Gallery'}
          </h1>
          <p style={styles.gallerySub}>Browse your photos and create your perfect package.</p>
        </div>
      )}

      <header style={styles.header}>
        <div>
          {mode === 'direct' && (
            <h1 style={styles.title}>{activeStudentName ? `${activeStudentName}'s Photos` : 'Your Photos'}</h1>
          )}
        </div>
        <div style={styles.headerActions}>
          {!showSeasonCards && poses.length > 0 && (
            <div style={styles.count}>
              {readyCount}
              {' '}
              ready
              {pendingCount > 0 ? ` · ${pendingCount} processing` : ''}
            </div>
          )}
          {!showSeasonCards && startingPrice != null && allReadyPoses.length > 0 && (
            <button
              type="button"
              style={styles.packagesBtn}
              onClick={() => shopPackages(allReadyPoses[0])}
            >
              {`See packages · from $${startingPrice.toFixed(2)}`}
            </button>
          )}
        </div>
      </header>

      {studentList.length > 1 && (
        <div style={styles.studentBar}>
          <span style={styles.studentBarLabel}>Shopping for:</span>
          {studentList.map((st) => {
            const active = st.student_image_id === studentBaseId;
            return (
              <button
                key={st.student_image_id}
                type="button"
                style={active ? styles.studentTabActive : styles.studentTab}
                onClick={() => switchStudent(st.student_image_id)}
              >
                {st.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Flow landing: wait for the seasons fetch before deciding chooser-vs-poses
          (avoids a flash of the pose rail on first load). */}
      {seasonsPending && (
        <div style={styles.empty}><div style={styles.spinner} /></div>
      )}

      {/* Flow landing: the Figma season-card chooser (access-code season large,
          prior seasons smaller to the right). */}
      {showSeasonCards && <SeasonCarousel seasons={seasons} onOpen={openSeason} />}

      {/* Drilled into a season: a way back to the chooser. */}
      {mode === 'flow' && seasonOpen && seasons.length > 1 && (
        <button type="button" style={styles.back} onClick={backToSeasons}>← All sessions</button>
      )}

      {/* Direct page keeps the simple season tabs (no carousel). */}
      {mode === 'direct' && seasons.length > 1 && enteredLabel && !codeNoteHidden && (
        <div style={styles.codeNote}>
          <span style={styles.codeBannerStar} aria-hidden="true">★</span>
          <span>
            {'Viewing '}
            <strong>{enteredLabel}</strong>
            {' (your access-code year). Earlier years are below.'}
          </span>
          <button type="button" style={styles.codeNoteX} aria-label="Dismiss" onClick={dismissCodeNote}>×</button>
        </div>
      )}

      {mode === 'direct' && seasons.length > 1 && (
        <div style={styles.seasonTabs}>
          {seasons.map((s) => {
            const isActive = s.student_image_id === activeId;
            return (
              <button
                key={s.student_image_id}
                type="button"
                style={isActive ? styles.seasonTabActive : styles.seasonTab}
                title={s.current ? 'The season for the access code you entered' : undefined}
                onClick={() => switchSeason(s.student_image_id)}
              >
                {s.current && <span style={styles.seasonMarker} aria-hidden="true">★ </span>}
                {s.label}
              </button>
            );
          })}
        </div>
      )}

      {!showSeasonCards && !seasonsPending && (
      <>
        {favorites.size > 0 && (
        <div style={styles.favBar}>
          <button
            type="button"
            style={favoritesOnly ? styles.favToggleOn : styles.favToggle}
            onClick={() => setFavoritesOnly((v) => !v)}
          >
            {favoritesOnly ? `♥ Showing favorites (${favorites.size})` : `♡ Show favorites (${favorites.size})`}
          </button>
        </div>
        )}

        {status === 'loading' && (
        <div className="sg-grid" aria-busy="true" aria-label="Loading photos">
          {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map((k) => (
            <div key={k} className="sg-tile sg-tile--processing"><div className="sg-shimmer" /></div>
          ))}
        </div>
        )}

        {status === 'empty' && (
        <div style={styles.empty}>
          {isCurrent ? (
            <>
              <div style={styles.spinner} />
              <p style={styles.note}>
                Your photos are being prepared and will appear here automatically as they’re ready.
              </p>
            </>
          ) : (
            <p style={styles.note}>No photos found for this year.</p>
          )}
        </div>
        )}

        {status === 'error' && poses.length === 0 && (
        <p style={styles.note}>We had trouble loading your photos. Retrying…</p>
        )}

        {displayed.length > 0 && (() => {
          const cards = displayed.map((pose) => (
            <PoseCard
              key={pose.id}
              pose={pose}
              backgroundUrl={bgUrl}
              favorite={favorites.has(pose.id)}
              onToggleFavorite={toggleFavorite}
              onSelect={() => openLightbox(pose)}
            />
          ));
          return mode === 'flow'
            ? <PhotoRail>{cards}</PhotoRail>
            : <div className="sg-grid">{cards}</div>;
        })()}

        {favoritesOnly && displayed.length === 0 && (
        <p style={styles.note}>No favorites yet — tap the ♡ on a photo to save it here.</p>
        )}
      </>
      )}

      {/* Popular products grid (flow layout only), mirroring the Figma gallery's
          "Popular Photo Products" section (2-col mobile / 4-col desktop). */}
      {mode === 'flow' && !seasonsPending && featuredVariants.length > 0 && (
        <section style={styles.popular}>
          <h2 style={styles.popularTitle}>Popular Photo Products</h2>
          <div className="sg-popular-grid">
            {featuredVariants.map((v) => (
              <div key={v.id} style={styles.popularCard}>
                {allReadyPoses[0]?.watermark_url && (
                  <span
                    style={{
                      ...styles.popularThumb,
                      backgroundImage: `url(${allReadyPoses[0].watermark_url})`,
                      ...(bgUrl ? { backgroundColor: 'transparent' } : {}),
                    }}
                  />
                )}
                <span style={styles.popularName}>{v.name}</span>
                <span style={styles.popularPrice}>{v.price != null ? `$${v.price.toFixed(2)}` : ''}</span>
                <button
                  type="button"
                  style={styles.popularAdd}
                  disabled={!allReadyPoses.length}
                  onClick={() => allReadyPoses.length && shopPackages(allReadyPoses[0])}
                >
                  Shop this package
                </button>
              </div>
            ))}
          </div>
          {allReadyPoses.length > 0 && (
            <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
              <button type="button" style={styles.viewMore} onClick={() => shopPackages(allReadyPoses[0])}>
                View all photo packages
              </button>
            </div>
          )}
        </section>
      )}

      {/* Personalized cards & gifts (Printess editor). */}
      {mode === 'flow' && !seasonsPending && printess.length > 0 && allReadyPoses.length > 0 && (
        <section style={styles.popular}>
          <h2 style={styles.popularTitle}>Personalize a card or gift</h2>
          <div className="sg-popular-grid">
            {printess.map((product) => (
              <div key={product.id} style={styles.popularCard}>
                {allReadyPoses[0]?.watermark_url && (
                  <span
                    style={{
                      ...styles.popularThumb,
                      backgroundImage: `url(${allReadyPoses[0].watermark_url})`,
                      ...(bgUrl ? { backgroundColor: 'transparent' } : {}),
                    }}
                  />
                )}
                <span style={styles.popularName}>{product.name}</span>
                <span style={styles.popularPrice}>
                  {product.variants[0]?.price != null ? `$${product.variants[0].price.toFixed(2)}` : ''}
                </span>
                <button
                  type="button"
                  style={styles.popularAdd}
                  onClick={() => openPrintess(product, allReadyPoses[0])}
                >
                  Personalize
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Immediate feedback after "View & Order": the favorites lightbox opens
          once the season's poses are ready (see the pendingLightbox effect).
          Also covers the case where the lightbox is open but its poses are
          momentarily empty (a season swap), so it never blinks to a blank. */}
      {(pendingLightbox || (lightboxIndex != null && displayedReady.length === 0)) && (
        <div className="sg-lb-backdrop" role="status" aria-label="Loading your photos">
          <span className="sg-lb-spinner" />
        </div>
      )}

      {lightboxIndex != null && displayedReady.length > 0 && (
        <Lightbox
          poses={displayedReady}
          index={lightboxIndex}
          favorites={favorites}
          backgroundUrl={bgUrl}
          onIndex={setLightboxIndex}
          onToggleFavorite={toggleFavorite}
          onSelectAll={() => favoriteAll(displayedReady.map((p) => p.id))}
          onShop={(pose) => { setLightboxIndex(null); shopPackages(pose); }}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      <ToastStack toasts={toasts} onDismiss={dismissToast} />

      <CartBar itemCount={cart.item_count} total={cart.total} onOpen={() => setView('cart')} />
    </div>
  );
};

StoreGallery.propTypes = {
  posesUrl: PropTypes.string.isRequired,
  productsUrl: PropTypes.string.isRequired,
  templatesUrl: PropTypes.string.isRequired,
  seasonsUrl: PropTypes.string.isRequired,
  studentsUrl: PropTypes.string.isRequired,
  cartUrl: PropTypes.string.isRequired,
  cartItemsUrl: PropTypes.string.isRequired,
  couponUrl: PropTypes.string.isRequired,
  couponDeleteUrl: PropTypes.string.isRequired,
  checkoutUrl: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  studentName: PropTypes.string,
  currentStudentImageId: PropTypes.number.isRequired,
  printessToken: PropTypes.string,
  upsellSteps: PropTypes.arrayOf(PropTypes.string),
  mode: PropTypes.oneOf(['flow', 'direct']),
};

StoreGallery.defaultProps = {
  studentName: null,
  printessToken: '',
  upsellSteps: DEFAULT_UPSELL_STEPS,
  mode: 'flow',
};

export default StoreGallery;
